<script lang="ts">
	import OfferInfoCards from './OfferInfoCards.svelte'
	import { CalcMethod, LotType } from '$types/enums'
	import { t } from '$i18n/i18n'
	import type { Lot } from '../optimizer/lot'
	import RadioButton from '../atoms/RadioButton.svelte'
	import { getContext } from 'svelte'
	import { EmptyOffer, type Offer, type Offers } from './types/offer'
	import { RequestType } from './types/requestDto'
	import { type Writable } from 'svelte/store'
	import type { Deal } from './types/deal'
	import {
		areAllRejectedAndNotAllEmpty,
		isAnyOfferAccepted,
		isAnyOfferNotEmpty,
		isOfferProvidedAndOtherOfferNotAccepted,
		isOfferRejected,
		isOfferRejectedByOptimizer
	} from './offerUtils'

	let lot: Writable<Lot> = getContext('lot')
	const offers: Writable<Offers> = getContext('offers')
	const deal: Writable<Deal | null> = getContext('deal')

	let {
		selected = $bindable<RequestType | null>(null),
		selectable = false,
		onOptimizerPage = false
	}: {
		selected?: RequestType | null
		selectable?: boolean
		onOptimizerPage?: boolean
	} = $props()

	// We show both of them if anyone not SENT (OfferState.READY) OR if any SENT then show only this one
	let materialProvided = $derived(
		isOfferProvidedAndOtherOfferNotAccepted(
			$offers.material,
			RequestType.MATERIAL,
			$offers?.materialWithTransport,
			selectable,
			$lot.type
		)
	)
	let materialWithTranspProvided = $derived(
		isOfferProvidedAndOtherOfferNotAccepted(
			$offers.materialWithTransport,
			RequestType.MATERIAL_WITH_TRANSPORT,
			$offers?.material,
			selectable,
			$lot.type
		)
	)
	let transportProvided = $derived(
		isOfferProvidedAndOtherOfferNotAccepted(
			$offers.transport,
			RequestType.TRANSPORT,
			null,
			selectable,
			$lot.type
		)
	)

	let disableRadioButtons = $derived(
		selectable &&
			($lot.type === LotType.TRANSPORT ||
				(materialProvided && !materialWithTranspProvided) ||
				(!materialProvided && materialWithTranspProvided))
	)

	let localTransportOffer = $derived.by(() => {
		return getValidOfferByType(transportProvided, RequestType.TRANSPORT, $offers.transport)
	})
	let localMaterialWithTranspOffer = $derived.by(() => {
		return getValidOfferByType(
			materialWithTranspProvided,
			RequestType.MATERIAL_WITH_TRANSPORT,
			$offers.materialWithTransport
		)
	})
	let localMaterialOffer = $derived.by(() => {
		return getValidOfferByType(materialProvided, RequestType.MATERIAL, $offers.material)
	})

	let headerText = $derived.by(() => {
		// we now show the OfferInfo component on the optimizer page in state OPTIMIZED,
		// however we should not leak that the project is already accepted, thus we check if we are on the optimizer page
		if (isAnyOfferAccepted($offers) && $deal !== null && !onOptimizerPage) {
			return $t('UI.optimizerPage.offer.accepted.text')
		} else if (areAllRejectedAndNotAllEmpty($offers) && $deal === null) {
			return $t('UI.optimizerPage.offer.rejected.text')
		} else {
			const isAnyOfferNotEmptyResult = isAnyOfferNotEmpty($offers)
			const onOptimizerPageAndOfferExisting = onOptimizerPage && isAnyOfferNotEmptyResult
			const onOffererPageAndOfferExisting = selectable && isAnyOfferNotEmptyResult
			const prefix = onOptimizerPageAndOfferExisting
				? 'Abgegebenes '
				: onOffererPageAndOfferExisting
					? 'Eingegangenes '
					: ''
			return (
				prefix +
				($lot.calcMethod === CalcMethod.REQUEST_OFFER
					? $t('UI.optimizerPage.offer.text')
					: $t('UI.optimizerPage.counterOffer.text'))
			)
		}
	})

	// auto-selects when only one request type is available ($bindable prop mutation, not state derivation)
	$effect(() => {
		if (!selectable) return
		if ($lot.type === LotType.TRANSPORT) {
			selected = RequestType.TRANSPORT
		} else if (materialProvided && !materialWithTranspProvided) {
			selected = RequestType.MATERIAL
		} else if (!materialProvided && materialWithTranspProvided) {
			selected = RequestType.MATERIAL_WITH_TRANSPORT
		}
	})

	function getValidOfferByType(offerProvided: boolean, requestType: RequestType, offer: Offer) {
		if (!offerProvided) {
			return EmptyOffer(requestType)
		}
		if (!onOptimizerPage && isOfferRejected(offer)) {
			return EmptyOffer(requestType)
		}
		return offer
	}

	function getBlurText(offer: Offer, requestType: RequestType) {
		const payload = { requestType }
		return isOfferRejectedByOptimizer(offer)
			? $t('UI.offers.rejectedOfferBlur.Info', payload)
			: $t('UI.offers.noOfferBlur.Info', payload)
	}
</script>

<div style="width: 100%;">
	<div class="offer-facts-title">
		<h2>{@html headerText}</h2>
	</div>
	<div class="offer-facts-info__container">
		{#if $lot.type === LotType.TRANSPORT}
			<div
				class="transport offer-facts-info_header"
				class:selectable={selectable && transportProvided}
				class:offer-unselected={(selectable && selected !== RequestType.TRANSPORT) ||
					!transportProvided}
			>
				<div>
					{#if selectable && transportProvided}
						<div class="offer-radio-btn-container">
							<RadioButton
								bind:selected
								groupName="requestType"
								value={RequestType.TRANSPORT}
								disabled={disableRadioButtons}
							/>
						</div>
					{/if}
					<div class="offer-facts-info__title">
						<h3>{$t('RequestType.TRANSPORT')}</h3>
					</div>
				</div>
			</div>
			<OfferInfoCards offer={localTransportOffer} requestType={RequestType.TRANSPORT} />
			{#if !transportProvided || (!onOptimizerPage && isOfferRejected($offers?.transport))}
				<div class="transport offer-unselected-blur">
					<h2>{@html getBlurText($offers?.transport, $t('RequestType.TRANSPORT'))}</h2>
				</div>
			{/if}
		{:else}
			<div
				class="material_with_transport offer-facts-info_header"
				class:selectable={selectable && materialWithTranspProvided}
				class:offer-unselected={(selectable && selected !== RequestType.MATERIAL_WITH_TRANSPORT) ||
					!materialWithTranspProvided}
			>
				<div>
					{#if selectable && materialWithTranspProvided}
						<div class="offer-radio-btn-container">
							<RadioButton
								bind:selected
								groupName="requestType"
								value={RequestType.MATERIAL_WITH_TRANSPORT}
								disabled={disableRadioButtons}
							/>
						</div>
					{/if}
					<div class="offer-facts-info__title">
						<h3>{$t('RequestType.MATERIAL_WITH_TRANSPORT')}</h3>
					</div>
				</div>
			</div>
			<OfferInfoCards
				offer={localMaterialWithTranspOffer}
				requestType={RequestType.MATERIAL_WITH_TRANSPORT}
			/>
			{#if !materialWithTranspProvided || (!onOptimizerPage && isOfferRejected($offers?.materialWithTransport))}
				<div class="material_with_transport offer-unselected-blur">
					<h2>
						{@html getBlurText(
							$offers?.materialWithTransport,
							$t('RequestType.MATERIAL_WITH_TRANSPORT')
						)}
					</h2>
				</div>
			{/if}
			<div
				class="material offer-facts-info_header"
				class:selectable={selectable && materialProvided}
				class:offer-unselected={(selectable && selected !== RequestType.MATERIAL) ||
					!materialProvided}
			>
				<div>
					{#if selectable && materialProvided}
						<div class="offer-radio-btn-container">
							<RadioButton
								bind:selected
								groupName="requestType"
								value={RequestType.MATERIAL}
								disabled={disableRadioButtons}
							/>
						</div>
					{/if}
					<div class="offer-facts-info__title">
						<h3>{$t('RequestType.MATERIAL')}</h3>
					</div>
				</div>
			</div>
			<OfferInfoCards offer={localMaterialOffer} requestType={RequestType.MATERIAL} />
			{#if !materialProvided || (!onOptimizerPage && isOfferRejected($offers?.material))}
				<div class="material offer-unselected-blur">
					<h2>{@html getBlurText($offers?.material, $t('RequestType.MATERIAL'))}</h2>
				</div>
			{/if}
		{/if}
	</div>
</div>

<style lang="scss">
	@use '$styles/global';

	.offer-facts-info__container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto;
		gap: 1.375rem 7rem;
		width: 100%;
	}

	.offer-facts-title {
		margin: 0 0 1rem;
		text-align: left;
	}

	.offer-facts-info__title {
		margin-left: 4px; // because of the border of the cards, we add a small margin

		& h3 {
			margin: 0;
		}
	}

	// in case we want to use it to select an offer (as offerer), we need to blur down one of the offers
	.selectable {
		:global(span) {
			transition: filter 0.4s;
			filter: none;
		}

		& > div:first-child {
			@include global.flex-row($justify: flex-start);
		}
	}

	.offer-facts-info_header {
		grid-row: 1/1;
	}

	.offer-unselected-blur {
		position: relative;
		opacity: 0.9;
		background: white;
		grid-row: 2/5;

		@include global.flex-row;

		padding: 25%;
		text-align: center;

		&.transport {
			grid-column: 1/-1;
			grid-row: 2/3;
			padding: unset;
		}
	}

	.material {
		grid-column: 2/2;
	}

	.material_with_transport,
	.transport {
		grid-column: 1/1;
	}

	/* Tablets */
	@media screen and (max-width: global.$medium) {
		.offer-facts-info__container {
			grid-template-columns: 1fr;
		}

		.material,
		.material_with_transport,
		.transport {
			grid-column: 1/1 !important;
		}

		.material.offer-facts-info_header {
			grid-row: 5/5;
		}

		.material.offer-unselected-blur {
			grid-row: 6/9;
		}

		.transport.offer-unselected-blur {
			grid-row: 2/4;
		}

		.material.offer-unselected {
			display: none;

			& :global(+ div.offer-facts-info__data) {
				display: none; // we can hide them, since we show offers below each other on mobile and would just create a huge whitespace
			}
		}
	}
</style>
